import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { sowsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface SowImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Upload a SOW workbook.
 *
 * `.xlsx` ONLY, unlike every other importer in the app. A CSV holds a single
 * table, and this file is four sheets whose rows reference each other — a CSV
 * would import the agreements and drop every engagement without saying so.
 * The server refuses one; the picker should not offer it in the first place.
 */
export function SowImportSheet({ open, onOpenChange }: SowImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import SOWs"
      accept=".xlsx"
      acceptedExtensions={['.xlsx']}
      description={
        <>
          Upload an .xlsx with the <strong>SOW</strong>, <strong>Retainers</strong>,{' '}
          <strong>T&amp;M</strong> and <strong>Metrics</strong> sheets — one row per record, with a
          column per activity and per priced role. Each SOW applies whole, engagements included. A
          SOW already matching the file is left alone rather than amended, and one absent from the
          file is untouched.
        </>
      }
      onImport={async (file, { idempotencyKey }) => {
        const res = await sowsApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          // A re-uploaded export is all of these. Folding them into `updated`
          // would claim two hundred amendments that never happened.
          unchanged: res.data.unchanged,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            // Which tab the row number counts on — a failure on Retainers is
            // not one the reader will find on the SOW sheet.
            sheetName: r.sheet,
            outcome: r.outcome,
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        // An import amends SOWs, which re-stamps the timesheets under them and
        // moves what budgets are measured against, so the SOW cache alone is
        // not the whole blast radius.
        queryClient.invalidateQueries({ queryKey: queryKeys.sows.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.customerBudgets.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await sowsApi.exportTemplate();
          triggerDownload(blob, filename ?? 'sows-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}
